import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import {
  CATEGORIES,
  CATEGORY_META,
  PRODUCTS,
  PRODUCT_THEME,
  VISIBILITY_META,
} from '../lib/products';
import { cx } from '../lib/format';
import type { Category, Product, User, Visibility } from '../lib/types';
import { CloseIcon, ImageIcon, PriorityMeter, Spinner } from './primitives';
import { useToast } from './Toast';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export function NewSuggestionModal({
  open,
  onClose,
  onCreated,
  people,
  isAdmin,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  people: User[];
  isAdmin: boolean;
}) {
  const toast = useToast();

  const [product, setProduct] = useState<Product>('cpu_neevcloud');
  const [category, setCategory] = useState<Category>('feature_request');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState(3);
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [assignedToId, setAssignedToId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const fileInput = useRef<HTMLInputElement>(null);
  const firstField = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  /* Reset on open so a re-opened form is never stale. */
  useEffect(() => {
    if (!open) return;
    setProduct('cpu_neevcloud');
    setCategory('feature_request');
    setTitle('');
    setDescription('');
    setPriority(3);
    setVisibility('public');
    setAssignedToId('');
    setFile(null);
    setPreview(null);
    setFormError(null);
    setFieldErrors({});
    const id = window.setTimeout(() => firstField.current?.focus(), 120);
    return () => window.clearTimeout(id);
  }, [open]);

  /* Object URLs must be revoked or they leak for the page's lifetime. */
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  /* Escape closes; Tab is trapped inside the dialog. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose, submitting]);

  function acceptFile(f: File | undefined | null) {
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast('Only image files can be attached.', 'error');
      return;
    }
    if (f.size > MAX_IMAGE_BYTES) {
      toast('That image is larger than 5MB. Please pick a smaller one.', 'error');
      return;
    }
    setFile(f);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;

    setFormError(null);
    setFieldErrors({});

    // Mirror the server rules so the user gets feedback without a round trip.
    const local: Record<string, string> = {};
    if (title.trim().length < 5) local.title = 'Give it a title of at least 5 characters.';
    if (description.trim().length < 10) local.description = 'Add a little more detail.';
    if (Object.keys(local).length > 0) {
      setFieldErrors(local);
      return;
    }

    setSubmitting(true);
    try {
      const form = new FormData();
      form.set('product', product);
      form.set('category', category);
      form.set('title', title.trim());
      form.set('description', description.trim());
      form.set('priority', String(priority));
      form.set('visibility', visibility);
      if (isAdmin && assignedToId) form.set('assignedToId', assignedToId);
      if (file) form.set('image', file);

      await api.create(form);
      toast('Suggestion submitted. Thank you!', 'success');
      onCreated();
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
        if (err.fields) setFieldErrors(err.fields);
      } else {
        setFormError('Could not submit right now. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-ink-950/80 backdrop-blur-sm"
            onClick={() => !submitting && onClose()}
          />

          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-suggestion-title"
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 340, damping: 32 }}
            className="surface-raised relative flex max-h-[92dvh] w-full max-w-2xl flex-col overflow-hidden rounded-b-none sm:rounded-card"
          >
            <header className="hairline flex items-start justify-between gap-4 border-b px-6 py-5">
              <div>
                <h2 id="new-suggestion-title" className="text-h2 font-semibold text-mist-50">
                  Share a suggestion
                </h2>
                <p className="mt-1 text-sm text-mist-400">
                  A bug, a piece of customer feedback, or an idea worth building.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="btn-subtle -mt-1 -mr-2 p-2"
                aria-label="Close"
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </header>

            <form onSubmit={submit} className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
              {formError && (
                <div
                  role="alert"
                  className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200"
                >
                  {formError}
                </div>
              )}

              {/* Product */}
              <fieldset>
                <legend className="label">Product</legend>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {PRODUCTS.map((p, i) => {
                    const t = PRODUCT_THEME[p];
                    const active = product === p;
                    return (
                      <button
                        key={p}
                        ref={i === 0 ? firstField : undefined}
                        type="button"
                        onClick={() => setProduct(p)}
                        aria-pressed={active}
                        className={cx(
                          'flex flex-col items-start gap-2 rounded-xl border px-3 py-3 text-left transition-all duration-150',
                          active ? 'bg-[var(--tint-bg-strong)]' : 'chip-idle',
                        )}
                        style={
                          active
                            ? { borderColor: `${t.hex}66`, boxShadow: `0 0 0 3px ${t.hex}1f` }
                            : undefined
                        }
                      >
                        <span className={cx('h-2 w-2 rounded-full', t.dot)} />
                        <span
                          className={cx(
                            'text-xs leading-tight font-semibold',
                            active ? 'text-mist-50' : 'text-mist-400',
                          )}
                        >
                          {t.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              {/* Category */}
              <fieldset>
                <legend className="label">Type</legend>
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCategory(c)}
                      aria-pressed={category === c}
                      className={cx(
                        'chip cursor-pointer transition-all duration-150',
                        category === c
                          ? 'border-orchid-400/50 bg-orchid-400/14 text-orchid-300'
                          : 'chip-idle text-mist-500 hover:text-mist-200',
                      )}
                    >
                      <span aria-hidden="true">{CATEGORY_META[c].icon}</span>
                      {CATEGORY_META[c].label}
                    </button>
                  ))}
                </div>
              </fieldset>

              {/* Title */}
              <div>
                <label htmlFor="s-title" className="label">
                  Title
                </label>
                <input
                  id="s-title"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    if (fieldErrors.title) setFieldErrors((f) => ({ ...f, title: '' }));
                  }}
                  maxLength={140}
                  placeholder="Summarise it in one line"
                  aria-invalid={Boolean(fieldErrors.title)}
                  aria-describedby={fieldErrors.title ? 's-title-err' : undefined}
                  className="field"
                />
                <div className="mt-1.5 flex justify-between gap-3">
                  {fieldErrors.title ? (
                    <p id="s-title-err" className="text-xs text-rose-300">
                      {fieldErrors.title}
                    </p>
                  ) : (
                    <span />
                  )}
                  <p className="text-xs text-mist-600 tabular-nums">{title.length}/140</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="s-desc" className="label">
                  The point you want to make
                </label>
                <textarea
                  id="s-desc"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (fieldErrors.description)
                      setFieldErrors((f) => ({ ...f, description: '' }));
                  }}
                  rows={5}
                  maxLength={5000}
                  placeholder="What did you notice, what should change, and why does it matter?"
                  aria-invalid={Boolean(fieldErrors.description)}
                  aria-describedby={fieldErrors.description ? 's-desc-err' : undefined}
                  className="field resize-y leading-relaxed"
                />
                <div className="mt-1.5 flex justify-between gap-3">
                  {fieldErrors.description ? (
                    <p id="s-desc-err" className="text-xs text-rose-300">
                      {fieldErrors.description}
                    </p>
                  ) : (
                    <span />
                  )}
                  <p className="text-xs text-mist-600 tabular-nums">{description.length}/5000</p>
                </div>
              </div>

              {/* Image */}
              <div>
                <span className="label">Screenshot (optional)</span>
                {preview ? (
                  <div className="hairline relative overflow-hidden rounded-xl border">
                    <img src={preview} alt="Selected attachment preview" className="max-h-56 w-full object-cover" />
                    <div className="flex items-center justify-between gap-3 bg-ink-900/80 px-3 py-2">
                      <p className="truncate text-xs text-mist-400">
                        {file?.name} · {((file?.size ?? 0) / 1024).toFixed(0)} KB
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setFile(null);
                          if (fileInput.current) fileInput.current.value = '';
                        }}
                        className="btn-subtle shrink-0 px-2 py-1 text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <label
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragging(false);
                      acceptFile(e.dataTransfer.files?.[0]);
                    }}
                    className={cx(
                      'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-6 py-8 text-center transition-colors duration-150',
                      dragging ? 'border-lotus-400/60 bg-lotus-400/8' : 'chip-idle',
                    )}
                  >
                    <ImageIcon className="h-6 w-6 text-mist-500" />
                    <span className="text-sm font-medium text-mist-200">
                      Drop an image here, or click to browse
                    </span>
                    <span className="text-xs text-mist-600">PNG, JPEG, WebP or GIF · up to 5MB</span>
                    <input
                      ref={fileInput}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="sr-only"
                      onChange={(e) => acceptFile(e.target.files?.[0])}
                    />
                  </label>
                )}
              </div>

              {/* Impact + visibility */}
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <span className="label">Impact</span>
                  <PriorityMeter value={priority} onChange={setPriority} size="lg" />
                  <p className="mt-2 text-xs leading-relaxed text-mist-600">
                    How much does this affect customers or the team?
                  </p>
                </div>

                <div>
                  <span className="label">Visibility</span>
                  <div className="hairline flex rounded-xl border bg-ink-900/60 p-0.5">
                    {(['public', 'private'] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setVisibility(v)}
                        aria-pressed={visibility === v}
                        className={cx(
                          'flex-1 rounded-[0.625rem] px-3 py-2 text-xs font-semibold transition-colors duration-150',
                          visibility === v
                            ? v === 'private'
                              ? 'bg-amber-400/18 text-amber-200'
                              : 'bg-[var(--tint-bg-strong)] text-mist-50'
                            : 'text-mist-500 hover:text-mist-200',
                        )}
                      >
                        {VISIBILITY_META[v].label}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-mist-600">
                    {VISIBILITY_META[visibility].hint}.
                  </p>
                </div>
              </div>

              {isAdmin && people.length > 0 && (
                <div>
                  <label htmlFor="s-assignee" className="label">
                    Assign to (optional)
                  </label>
                  <select
                    id="s-assignee"
                    value={assignedToId}
                    onChange={(e) => setAssignedToId(e.target.value)}
                    className="field"
                  >
                    <option value="">Leave unassigned for triage</option>
                    {people.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {p.title ? ` — ${p.title}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </form>

            <footer className="hairline flex items-center justify-end gap-3 border-t bg-ink-900/50 px-6 py-4">
              <button type="button" onClick={onClose} disabled={submitting} className="btn-ghost">
                Cancel
              </button>
              <button type="submit" onClick={submit} disabled={submitting} className="btn-primary min-w-36">
                {submitting ? (
                  <>
                    <Spinner className="h-4 w-4" /> Submitting…
                  </>
                ) : (
                  'Submit suggestion'
                )}
              </button>
            </footer>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
